/**
 * Umami 自定义事件埋点工具
 *
 * @description 封装 window.umami.track，供业务代码自主上报自定义事件。
 * 脚本未加载（如被广告拦截插件屏蔽）时静默跳过，不影响业务逻辑。
 * @doc https://docs.umami.is/docs/track-events
 */

// Umami 官方限制：事件名 50 字符，字符串属性值 500 字符
const MAX_EVENT_NAME_LENGTH = 50;
const MAX_STRING_VALUE_LENGTH = 500;

type TrackEventData = Record<string, unknown>;

declare global {
  interface Window {
    umami?: {
      track: (eventName: string, data?: TrackEventData) => void;
      identify: (uniqueId: string, data?: TrackEventData) => void;
    };
  }
}

const truncate = (value: string, maxLength: number): string =>
  value.length > maxLength ? value.slice(0, maxLength) : value;

const sanitizeData = (data?: TrackEventData): TrackEventData | undefined => {
  if (!data) {
    return undefined;
  }
  const sanitized: TrackEventData = {};
  for (const [key, value] of Object.entries(data)) {
    sanitized[key] =
      typeof value === 'string'
        ? truncate(value, MAX_STRING_VALUE_LENGTH)
        : value;
  }
  return sanitized;
};

/**
 * 上报一个 Umami 自定义事件
 *
 * @param name 事件名，建议使用「模块-动作」格式，如「人才搜索-提交搜索」
 * @param data 可选附加属性，可在 Umami 后台 Properties 面板中筛选
 */
export function trackEvent(name: string, data?: TrackEventData): void {
  try {
    const eventName = truncate(String(name).trim(), MAX_EVENT_NAME_LENGTH);
    if (!eventName) {
      return;
    }
    const eventData = sanitizeData(data);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[track]', eventName, eventData);
    }

    window.umami?.track(eventName, eventData);
  } catch {
    // 埋点失败不影响业务
  }
}

/**
 * 将当前登录用户绑定到 Umami 会话，之后该会话的所有事件都可按此 ID 筛选
 *
 * @param uniqueId 用户唯一标识（如 uuid）
 * @param data 可选会话级属性，如用户名、租户
 */
export function identifyUser(uniqueId: string, data?: TrackEventData): void {
  try {
    const id = String(uniqueId).trim();
    if (!id) {
      return;
    }
    const sessionData = sanitizeData(data);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[track] identify', id, sessionData);
    }

    window.umami?.identify(id, sessionData);
  } catch {
    // 埋点失败不影响业务
  }
}
